<script setup>
import { computed } from 'vue'
import Icon from '@/components/Icon.vue'
import { mdiChevronRight } from '@mdi/js'

const props = defineProps({
  titleStack: {
    type: Array,
    default: () => []
  },
  goTo: {
    type: Array,
    default: () => []
  },
  subtitle: {
    type: String,
    default: null
  }
})

// The last crumb is the page title; everything before it is the trail.
const trail = computed(() => props.titleStack.slice(0, -1))
const current = computed(() => props.titleStack[props.titleStack.length - 1] ?? '')
</script>

<template>
  <section class="pb-6">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div class="min-w-0">
        <nav
          v-if="trail.length"
          class="flex items-center flex-wrap gap-1 mb-1 text-sm text-gray-500 dark:text-gray-400"
          aria-label="Breadcrumb"
        >
          <template v-for="(title, index) in trail" :key="index">
            <router-link
              v-if="goTo[index]"
              :to="goTo[index]"
              class="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              {{ title }}
            </router-link>
            <span v-else>{{ title }}</span>
            <icon :path="mdiChevronRight" :size="14" w="w-4" h="h-4" class="text-gray-300 dark:text-gray-600" />
          </template>
        </nav>

        <!-- Wrap rather than ellipsise on phones: a long page title is the one
             thing on the screen the user needs in full, and below `sm` the row
             is `flex-col` so extra lines only push the actions down. From `sm`
             the actions sit alongside, so truncation comes back to protect them. -->
        <h1 class="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 break-words sm:truncate">
          {{ current }}
        </h1>

        <p v-if="subtitle || $slots.subtitle" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          <slot name="subtitle">{{ subtitle }}</slot>
        </p>
      </div>

      <!-- Wraps below `sm`: two full-size actions ("Buy tokens" + "Start
           campaign") need ~324px, which overflows a 320px viewport. Only pin
           `flex-shrink-0` once there is room for them on one line. -->
      <div v-if="$slots.actions" class="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:flex-shrink-0">
        <slot name="actions" />
      </div>
    </div>
  </section>
</template>
